import React, { useState } from 'react';
import { Megaphone, Pin, ShieldAlert, Sparkles, Wand2 } from 'lucide-react';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { cn } from '@/lib/utils';
import { SurfaceCanvas } from '@/client/components/admin/Highlights/SurfaceCanvas';
import { SlotDetailSheet } from '@/client/components/admin/Highlights/SlotDetailSheet';
import { SURFACES, SlotDef, SurfaceKey, getSurface } from '@/client/types/highlights/slotRegistry';

const HIGHLIGHTS_PERMISSION = 'system.highlights.manage';

const AccessDenied: React.FC = () => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      Managing promoted highlights requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{HIGHLIGHTS_PERMISSION}</code> permission. Ask an
      administrator to grant it.
    </p>
  </div>
);

const LEGEND: { icon: React.ReactNode; label: string; desc: string; cls: string }[] = [
  { icon: <Sparkles className="h-3 w-3" />, label: 'Automatic', desc: 'Fills itself with matching listings', cls: 'bg-slate-100 text-slate-500' },
  { icon: <Pin className="h-3 w-3" />, label: 'Featured', desc: 'Something is hand-picked to show first', cls: 'bg-blue-50 text-blue-700' },
  { icon: <Sparkles className="h-3 w-3" />, label: 'Custom card', desc: 'Custom heading or cover set', cls: 'bg-violet-50 text-violet-700' },
  { icon: <Megaphone className="h-3 w-3" />, label: 'Promotions', desc: 'Paid banners, managed in Promotions', cls: 'bg-orange-50 text-orange-700' },
  { icon: <Wand2 className="h-3 w-3" />, label: 'Auto by category', desc: 'Follows what the shopper browses', cls: 'bg-slate-100 text-slate-600' },
];

const HighlightsPage: React.FC = () => {
  useBreadcrumbs([
    { label: 'Admin', href: '/admin' },
    { label: 'Promoted Highlights', href: '/admin/highlights' },
  ]);

  const { hasPermission, isSuperAdmin, isLoading: permLoading } = useHasPermission(HIGHLIGHTS_PERMISSION);
  const [surfaceKey, setSurfaceKey] = useState<SurfaceKey>('main');
  const [activeSlot, setActiveSlot] = useState<SlotDef | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  const permissionKnownAbsent = !permLoading && !isSuperAdmin && !hasPermission;
  if (permissionKnownAbsent) {
    return (
      <div className="p-6">
        <AccessDenied />
      </div>
    );
  }

  const surface = getSurface(surfaceKey);

  const openSlot = (slot: SlotDef) => {
    setActiveSlot(slot);
    setSheetOpen(true);
  };

  return (
    <div className="space-y-6 p-6">
      {/* Title */}
      <div>
        <div className="mb-1 flex items-center gap-3">
          <Sparkles className="h-7 w-7 text-blue-700" />
          <h1 className="text-2xl font-bold text-gray-900">Promoted Highlights</h1>
        </div>
        <p className="ml-10 text-sm text-gray-500">
          Pick a page, then click any spot to choose what shows there. Most spots fill themselves — you only step in
          when you want to feature something special.
        </p>
      </div>

      {/* Step 1 — pick a page */}
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">Choose a page</p>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {SURFACES.map((s) => {
            const selected = s.key === surfaceKey;
            return (
              <button
                key={s.key}
                type="button"
                onClick={() => setSurfaceKey(s.key)}
                className={cn(
                  'flex flex-col items-start gap-1 rounded-xl border-2 p-4 text-left transition-all',
                  selected
                    ? 'border-blue-500 bg-blue-50/60 shadow-sm'
                    : 'border-gray-100 bg-white hover:border-blue-200 hover:shadow-sm',
                )}
              >
                <span className="text-2xl leading-none">{s.glyph}</span>
                <span className={cn('mt-1 text-sm font-semibold', selected ? 'text-blue-800' : 'text-gray-900')}>
                  {s.name}
                </span>
                <span className="text-xs text-gray-500">{s.tagline}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 rounded-xl border border-gray-100 bg-white px-4 py-3">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Key</span>
        {LEGEND.map((l) => (
          <span key={l.label} className="flex items-center gap-1.5">
            <span className={cn('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold', l.cls)}>
              {l.icon}
              {l.label}
            </span>
            <span className="text-xs text-gray-500">{l.desc}</span>
          </span>
        ))}
      </div>

      {/* Step 2 — the page's spots, drawn where they live */}
      <SurfaceCanvas surface={surface} onOpenSlot={openSlot} />

      <SlotDetailSheet slot={activeSlot} surfaceName={surface.name} open={sheetOpen} onOpenChange={setSheetOpen} />
    </div>
  );
};

export default HighlightsPage;
